import {Component, HostListener, OnInit} from "@angular/core";
import {FormGroup} from "@angular/forms";
import {ADPResourceService} from "digital-core";
import {PasswordFormControls} from "../../../../shared/password/password-form/password-form.component";
import {PreInitSettingsService} from "../../../../core/services/settings/pre-init-setting.service";
import {SnackbarService} from "../../../../core/services/snackbar/snackbar.service";
import {AccountServiceV0} from "./services/account.service";
import {AccountSignUpDtoV0} from "src/app/core/services/sign-up/account-signup.dto";
import {GoogleAnalyticsService} from "src/app/core/services/google-tag-manager.service";
import {GTMOnboardingEvents, OnboardingSteps} from "src/app/model/google-tag-manager/api-status";
import { Router } from "@angular/router";

enum CreateAccountFlow {
  emailSetup,
  otpSetup,
  contactSetup,
  passwordSetup,
  successStep
}
export interface ValidateOtpResponseDtoV0 {
  isValidOTP: boolean;
  otpSuccessfullyValidatedToken?: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  primaryEmail: string;
  smsConsent: boolean;
  contractId: string;
}

@Component({
  selector: 'app-create-account-v0',
  templateUrl: './create-account.component.html',
  styleUrls: ['./create-account.component.scss']
})
export class CreateAccountComponentV0 implements OnInit {
  // For template access
  public CreateAccountFlow = CreateAccountFlow;
  public heading: string;
  public encryptedEmail: string;
  public maskedEmail: string;
  public email: string;
  public firstName: string;

  public encryptedOTP: string;
  private otpSuccessfullyVerifiedToken?: string;

  public currentStep = 0;
  public totalStep = 3;
  public currentScreenFlow: CreateAccountFlow = CreateAccountFlow.emailSetup;
  public contactInfo: ValidateOtpResponseDtoV0;
  public passwordForm = new FormGroup<PasswordFormControls>({});
  public signUpInProgress = false;
  public showContactInfo = false;

  public constructor(
    private resourceService: ADPResourceService,
    private accountService: AccountServiceV0,
    private preInitSettingsService: PreInitSettingsService,
    private snackbarService: SnackbarService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private router: Router,
  ) { }

  @HostListener('window:popstate', ['$event'])
  onPopState(event) {
    this.onClose();
  }
  
  public ngOnInit() {
    this.showContactInfo = this.resourceService.getPortalSetting('Signup_Create_Account_ShowContactInfo').Value.toLowerCase() === 'true';
    if (!this.showContactInfo) {
      this.totalStep = 2;
    }
  }

  public contactInfoSubmitted(validatedInfo: ValidateOtpResponseDtoV0) {
    this.contactInfo = validatedInfo;
    this.goToPasswordStep();
    this.currentStep = 3;
  }

  private goToPasswordStep() {
    this.currentScreenFlow = CreateAccountFlow.passwordSetup;
    this.heading = this.resourceService.getViewLabel('Signup_Create_Account_Password_Heading_Text');
    this.googleAnalyticsService.emitOnboardingEvent(GTMOnboardingEvents.InProcess, OnboardingSteps.CreatePassword);
  }

  private goToContactStep() {
    this.currentScreenFlow = CreateAccountFlow.contactSetup;
    this.heading = this.resourceService.getViewLabel('Signup_Create_Account_Contact_Heading_Text');
  }

  private goToVerifyOtpStep() {
    this.heading = this.resourceService.getViewLabel('Signup_Create_Account_VerifyOTP_Heading_Text');
    this.currentScreenFlow = CreateAccountFlow.otpSetup;
  }

  public createOTPResponse(event) {
    this.encryptedEmail = event.email.substring(event.email.indexOf(':')+1);
    this.maskedEmail = event.email.substring(0, event.email.indexOf(':'));
    this.email = event.reqEmail;
    this.firstName = event.firstName;
    this.encryptedOTP = event.encryptedOTP;

    this.goToVerifyOtpStep();
    this.currentStep = 1;
  }

  public verifyOTPResponse(event: ValidateOtpResponseDtoV0) {
    if (event?.isValidOTP ?? false) {
      this.otpSuccessfullyVerifiedToken = event.otpSuccessfullyValidatedToken;
      if (this.showContactInfo) {
        this.contactInfo = event;
        this.contactInfo.primaryEmail = this.email;
        this.goToContactStep();
      } else {
        this.goToPasswordStep();
      }
      this.currentStep = 2;
    }
  }

  public previousScreen() {
    switch (this.currentStep) {
      case 1:
        this.currentScreenFlow = CreateAccountFlow.emailSetup;
        break;
      case 2:
        this.goToVerifyOtpStep();
        this.currentStep--;
        break;
      case 3:
        if (this.showContactInfo) {
          this.goToContactStep();
        } else {
          this.goToVerifyOtpStep();
        }
        this.currentStep--;
        break;
    }
  }

  public signUp(): void {
    if (this.passwordForm.invalid || this.signUpInProgress) {
      return;
    }
    this.signUpInProgress = true;
    this.disableEnableInputs(true);

    const signupInfo: AccountSignUpDtoV0 = {
      primaryUserEmail: this.email,
      userContact: this.email,
      password: this.passwordForm.controls.password?.value,
      otpSuccessToken: this.otpSuccessfullyVerifiedToken,
      oktaPrefix: this.preInitSettingsService.getSiteSettings().oktaPrefix,
      groupId: this.preInitSettingsService.getSiteSettings().groupId,
    };

    this.accountService.signUpNewAccount(signupInfo).subscribe({
      next: () => {
        this.signUpInProgress = false;
        this.disableEnableInputs(false);
        this.snackbarService.showSimpleMessage(this.resourceService.getViewLabel('Signup_Create_Account_Successful_Snackbar_Message'));
        this.currentScreenFlow = CreateAccountFlow.successStep;
        this.googleAnalyticsService.emitOnboardingEvent(GTMOnboardingEvents.Complete, OnboardingSteps.CreatePassword);
        this.googleAnalyticsService.emitOnboardingEvent(GTMOnboardingEvents.Complete, OnboardingSteps.CreateLogin);
      },
      error: (err: any) => {
        const emitError = {
          message: err?.error?.Message,
          type: err?.error?.ExceptionType,
          code: err?.error?.ErrorCode,
        }
        this.googleAnalyticsService.emitOnboardingEvent(GTMOnboardingEvents.Failed, OnboardingSteps.CreateLogin, emitError);
        if (err.error && err.error.Message) {
          if (err.error.Message.toLowerCase().indexOf('password (400, e0000001):') > -1) {
            err.error.Message = this.resourceService.getViewLabel('Global_Error_Code_Password_ece0000001');
          }
            // TODO: ERROR : Add the Logic in API to raise Error with right ErrorCode
            err.error.CustomTitle = err.error.Message;
        }
        this.signUpInProgress = false;
        this.disableEnableInputs(false);

        throw err;
      },
    });
  }

  /**
   * This method will enable / disable the input fields depending on whether
   * your pass a true or a false in. When you pass a true in, then it will
   * disable the inputs. When you pass a false in, then it will enable the
   * inputs.
   *
   * @param disabled When disabled is true, then the input fields will be
   * disabled. When disabled is false, then the input fields will be enabled.
   */
  private disableEnableInputs(disabled: boolean) {
    if (disabled === true) {
      this.passwordForm.disable();
    } else {
      this.passwordForm.enable();
    }
  }
   onClose() {
    this.router.navigate(['/login'], { queryParamsHandling: 'preserve' }).then(
      () => { window.location.reload(); });
  }
}
